import React, { useState } from 'react';
import { useParams } from 'react-router';
import { isEmpty } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { ChevronRight } from 'react-feather';

import AddAssistance from 'components/shared/AddAssistance';
import { Spinner } from 'admin-components/components';
import { Column, Row } from 'components/shared/Layout';
import JourneyDigest from 'components/shared/JourneyDigest';
import Button from 'components/ui/Button';

import { GET_BOOKING } from 'store/queries';

import { calcSegmentPointsTotal } from 'utils/functions';
import { useManagedQuery } from 'utils/managedHooks';
import buildJourneyDigestShape from 'utils/buildJourneyDigestShape';
import { nonCancelledExtras, totalExtras } from 'utils/payment';
import { hasTrainSegment } from '../BookingDetails/Extras/Extras.utils';
import { getTotalAddonPrice } from '../JourneySearch/JourneySearch.utils';
import { isBookingMiceGroup } from 'components/ui/BookingPathReviewBookingSummary/BookingPathReviewBookingSummary.utils';
import { getInfantPassengersFromDetails } from 'utils/getInfantPassengersFromDetails';

const Index = () => {
  const [isLoading, setLoading] = useState(false);
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(true);
  const { reference } = useParams();

  const { loading, data, refetch } = useManagedQuery(GET_BOOKING, {
    variables: { reference }
  });

  if (loading) {
    return (
      <Column alignItems="center" justifyContent="center">
        <Spinner />
      </Column>
    );
  }

  const {
    currency,
    eurostarInbound,
    eurostarOutbound,
    inbound,
    inboundTotalPrice,
    outbound,
    outboundTotalPrice,
    passengers,
    provisionalOfflineFees,
    totalPrice,
    outboundOrigin,
    outboundDestination,
    outboundExtras,
    inboundExtras,
    additionalDetails,
    isLoyalty
  } = data.booking;

  const isBookingMice = isBookingMiceGroup({ details: additionalDetails });
  const infantCount = getInfantPassengersFromDetails({ additionalDetails }).length;
  const outboundTotalPoints = calcSegmentPointsTotal(eurostarOutbound);
  const inboundTotalPoints = calcSegmentPointsTotal(eurostarInbound);

  const journeyDigest = {
    inboundShape: buildJourneyDigestShape(inbound),
    outboundShape: buildJourneyDigestShape(outbound),
    offlineFee: provisionalOfflineFees && provisionalOfflineFees[0],
    totalPrice,
    currency,
    inboundTotalPrice,
    outboundTotalPrice,
    isReturn: !isEmpty(eurostarOutbound) && !isEmpty(eurostarInbound)
  };

  const isExtrasOnlyBooking =
    !hasTrainSegment(data?.booking) && Boolean(data?.booking?.outboundExtras?.length || data?.booking?.inboundExtras?.length);

  const { outboundTotalAddOnPrice = 0, inboundTotalAddOnPrice = 0 } = getTotalAddonPrice({
    outbound,
    inbound
  });

  const nonCancelledOutboundExtras = nonCancelledExtras(outboundExtras);
  const nonCancelledInboundExtras = nonCancelledExtras(inboundExtras);
  const outboundTotalExtrasPrice = totalExtras(nonCancelledOutboundExtras);
  const inboundTotalExtrasPrice = totalExtras(nonCancelledInboundExtras);

  return (
    <>
      <Row start={2} span={14}>
        <AddAssistance
          setLoading={setLoading}
          bookingData={data.booking}
          refetchBooking={refetch}
          setIsConfirmDisabled={setIsConfirmDisabled}
          isReturn={journeyDigest.isReturn}
          shouldUseReviewBookingScreen={!isExtrasOnlyBooking}
          showAssistance={true}
          showHotel={true}
        />
      </Row>

      <JourneyDigest
        currency={journeyDigest.currency}
        confirmButton={
          <Button type="submit" loading={isLoading} disabled={isConfirmDisabled} data-cy="bp-continue" form="add-assistance-form">
            <FormattedMessage id="bookingPath.reviewBooking" />
            <ChevronRight />
          </Button>
        }
        inbound={journeyDigest.inboundShape}
        inboundTotalPrice={journeyDigest.inboundTotalPrice + inboundTotalAddOnPrice + inboundTotalExtrasPrice}
        outboundTotalPoints={outboundTotalPoints}
        isReturnJourney={journeyDigest.isReturn}
        offlineFee={journeyDigest.offlineFee}
        outbound={journeyDigest.outboundShape}
        outboundTotalPrice={journeyDigest.outboundTotalPrice + outboundTotalAddOnPrice + outboundTotalExtrasPrice}
        inboundTotalPoints={inboundTotalPoints}
        passengers={passengers}
        totalPrice={journeyDigest.totalPrice}
        originUIC={outboundOrigin?.uic}
        destinationUIC={outboundDestination?.uic}
        outboundExtras={nonCancelledOutboundExtras}
        inboundExtras={nonCancelledInboundExtras}
        isBookingMice={isBookingMice}
        infantCount={infantCount}
        isLoyaltyBooking={isLoyalty}
      />
    </>
  );
};

export default Index;
